import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Slider,
  Chip,
  LinearProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { AxisBottom } from '@visx/axis';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import pathfinderApi, { 
  CoTravelerResult, 
  CoTravelerAnalysisParams, 
  PathfinderDataPoint 
} from '../../services/pathfinderApi';

interface EnhancedCoTravelerAnalysisProps {
  dataPoints: PathfinderDataPoint[];
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>;
  dateRange: { start: Date | null; end: Date | null } | null;
  selectedCourse?: string;
  coTravelerResults?: CoTravelerResult[];
  coTravelerVisibility?: Record<string, boolean>;
  onVisualizationUpdate?: (results: CoTravelerResult[]) => void;
  onVisibilityChange?: (visibility: Record<string, boolean>) => void;
  /** When true, an "AdTech (Venntel)" source is offered; the sliders then drive
   *  the commercial mobile-location co-traveler query instead of GPS tracks. */
  adtechEnabled?: boolean;
}

// Entity type colors. WIFI/BLUETOOTH/CELLULAR are emitted by the digital
// co-traveler path (RF signals from phone-observed APs / BT devices / towers).
const ENTITY_COLORS: Record<string, string> = {
  SRP: '#f44336',
  RP: '#ff9800',
  Student: '#9c27b0',
  WIFI: '#4caf50',
  BLUETOOTH: '#2196f3',
  CELLULAR: '#ffc107',
  ADTECH: '#e91e63',
  Unknown: '#9e9e9e',
};

interface TooltipData {
  entity: string;
  entityType: string;
  hour: number;
  encounters: number;
  date: string;
}

const EnhancedCoTravelerAnalysis: React.FC<EnhancedCoTravelerAnalysisProps> = ({
  dataPoints,
  selectedStudents,
  selectedDevices,
  dateRange,
  selectedCourse,
  coTravelerResults: _coTravelerResults = [] as CoTravelerResult[],
  onVisualizationUpdate,
  adtechEnabled = false,
}) => {
  const theme = useTheme();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  // Analysis source: GPS-track co-travelers (default) or AdTech (Venntel).
  const [source, setSource] = useState<'track' | 'adtech'>('track');
  
  const [params, setParams] = useState<CoTravelerAnalysisParams>({
    deviceIds: [],
    dateRange: [new Date(), new Date()],
    proximityThreshold: 100,
    timeThreshold: 5,
    courseId: selectedCourse,
  });

  // Tooltip setup
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = 
    useTooltip<TooltipData>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  // Auto-update device IDs when selection changes
  useEffect(() => {
    const deviceIds = selectedStudents.flatMap(studentId => 
      selectedDevices[studentId] || []
    );
    setParams(prev => ({ ...prev, deviceIds }));
  }, [selectedStudents, selectedDevices]);

  // Auto-update date range
  useEffect(() => {
    if (dateRange && dateRange.start && dateRange.end) {
      setParams(prev => ({ ...prev, dateRange: [dateRange.start!, dateRange.end!] }));
    }
  }, [dateRange]);

  // Update course ID
  useEffect(() => {
    setParams(prev => ({ ...prev, courseId: selectedCourse }));
  }, [selectedCourse]);

  const handleStartAnalysis = useCallback(async () => {
    // AdTech source keys off the selected entities (not per-device tracks) and
    // reuses the proximity/time sliders as the search radius / time window.
    if (source === 'adtech') {
      if (adtechEnabled && selectedStudents.length > 0 && params.dateRange[0] && params.dateRange[1]) {
        setIsAnalyzing(true);
        setProgress(-1);
        setLoadingMessage('Querying AdTech co-travelers...');
        try {
          const adtech = await pathfinderApi.analyzeAdtechCoTravelers({
            entityIds: selectedStudents,
            dateRange: params.dateRange,
            radiusMeters: params.proximityThreshold,
            timeThresholdMinutes: params.timeThreshold,
          });
          const primaryDevice = selectedStudents[0] || 'Primary';
          const results: CoTravelerResult[] = (adtech.candidates || []).map((c) => {
            const conf = Math.max(0, Math.min(1, c.confidence || 0));
            const encounters = (c.locations || []).map((loc) => ({
              timestamp: new Date(loc.recorded_at),
              location: [Number(loc.latitude), Number(loc.longitude)] as [number, number],
              duration: 0,
              proximity: 0,
              confidence: conf,
              locationName: c.display_name || undefined,
            }));
            const firstSeen = c.first_seen ? new Date(c.first_seen).getTime() : 0;
            const lastSeen = c.last_seen ? new Date(c.last_seen).getTime() : firstSeen;
            return {
              primaryDevice,
              coTravelerDevice: c.external_id,
              coTravelerEntityType: 'ADTECH' as const,
              encounters,
              totalEncounters: Number(c.sighting_count ?? encounters.length),
              totalDuration: Math.max(0, (lastSeen - firstSeen) / 60000),
              averageProximity: 0,
              confidenceScore: conf,
              temporalOverlap: 0,
              spatialOverlap: 0,
              frequentLocations: [],
            };
          });
          onVisualizationUpdate?.(results);
          setProgress(100);
          setLoadingMessage(
            `Found ${results.length} AdTech co-travelers from ${adtech.total_signals_scanned} signals`,
          );
        } catch (error: any) {
          console.error('AdTech analysis error:', error);
          setLoadingMessage(error?.message || 'AdTech analysis failed');
        } finally {
          setIsAnalyzing(false);
        }
      }
      return;
    }

    if (params.deviceIds.length === 0) return;

    setIsAnalyzing(true);
    setProgress(-1);
    setLoadingMessage('Starting server-side analysis...');

    try {
      const results = await pathfinderApi.analyzeCoTravelersCrossAreas(params);
      onVisualizationUpdate?.(results);
      setProgress(100);
      setLoadingMessage(`Found ${results.length} co-travelers`);
    } catch (error) {
      console.error('Analysis error:', error);
      setLoadingMessage('Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  }, [params, onVisualizationUpdate, source, adtechEnabled, selectedStudents]);

  const handleStopAnalysis = useCallback(() => {
    setIsAnalyzing(false);
    setLoadingMessage('Analysis stopped');
  }, []);

  const handleExportResults = useCallback(() => {
    if (_coTravelerResults.length === 0) return;
    
    const csvRows = [
      ['Primary Device', 'Co-Traveler', 'Entity Type', 'Encounters', 'Total Duration (min)', 'Confidence', 'Avg Proximity (m)'].join(','),
      ..._coTravelerResults.map(r => [
        r.primaryDevice,
        r.coTravelerDevice,
        r.coTravelerEntityType,
        r.totalEncounters,
        Math.round(r.totalDuration),
        (r.confidenceScore * 100).toFixed(1) + '%',
        r.averageProximity.toFixed(1)
      ].join(','))
    ];
    
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `co-travelers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [_coTravelerResults]);

  // Process data for timeline heatmap
  const heatmapData = useMemo(() => {
    if (_coTravelerResults.length === 0) return { entities: [], hourlyData: [], maxCount: 0 };

    // Get unique co-travelers sorted by total encounters
    const entityMap = new Map<string, { type: string; totalEncounters: number }>();
    _coTravelerResults.forEach(result => {
      const existing = entityMap.get(result.coTravelerDevice);
      if (!existing || result.totalEncounters > existing.totalEncounters) {
        entityMap.set(result.coTravelerDevice, {
          type: result.coTravelerEntityType,
          totalEncounters: result.totalEncounters
        });
      }
    });

    const entities = Array.from(entityMap.entries())
      .sort((a, b) => b[1].totalEncounters - a[1].totalEncounters)
      .slice(0, 15) // Top 15
      .map(([id, data]) => ({ id, type: data.type }));

    // Create hourly encounter counts
    const hourlyData: { entity: string; entityType: string; hour: number; count: number; date: string }[] = [];
    let maxCount = 0;

    entities.forEach(entity => {
      const hourCounts = new Map<number, { count: number; date: string }>();
      
      _coTravelerResults
        .filter(r => r.coTravelerDevice === entity.id)
        .forEach(result => {
          (result.encounters || []).forEach(enc => {
            const hour = enc.timestamp.getHours();
            const existing = hourCounts.get(hour) || { count: 0, date: enc.timestamp.toLocaleDateString() };
            existing.count++;
            hourCounts.set(hour, existing);
          });
        });

      hourCounts.forEach((data, hour) => {
        hourlyData.push({
          entity: entity.id,
          entityType: entity.type,
          hour,
          count: data.count,
          date: data.date
        });
        if (data.count > maxCount) maxCount = data.count;
      });
    });

    return { entities, hourlyData, maxCount };
  }, [_coTravelerResults]);

  // Responsive chart dimensions
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(400);
  
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const containerWidth = chartContainerRef.current.offsetWidth;
        setChartWidth(Math.max(300, containerWidth - 16)); // 16px for padding
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    // Use ResizeObserver for more reliable container size detection
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateWidth);
      resizeObserver.disconnect();
    };
  }, []);

  // Chart dimensions
  const width = chartWidth;
  const height = Math.max(150, heatmapData.entities.length * 24 + 50);
  const margin = { top: 15, right: 20, bottom: 35, left: 100 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Scales
  const xScale = scaleLinear({
    domain: [0, 23],
    range: [0, innerWidth],
  });

  const yScale = scaleBand({
    domain: heatmapData.entities.map(e => e.id),
    range: [0, innerHeight],
    padding: 0.2,
  });

  const colorScale = scaleLinear({
    domain: [0, heatmapData.maxCount],
    range: [0.1, 1],
  });

  // Summary stats
  const summaryStats = useMemo(() => {
    if (_coTravelerResults.length === 0) return null;
    
    const byType = { SRP: 0, RP: 0, Student: 0, WIFI: 0, BLUETOOTH: 0, CELLULAR: 0, Unknown: 0 };
    _coTravelerResults.forEach(r => {
      const type = r.coTravelerEntityType as keyof typeof byType;
      if (type in byType) byType[type]++;
      else byType.Unknown++;
    });

    const totalEncounters = _coTravelerResults.reduce((sum, r) => sum + r.totalEncounters, 0);
    
    return { byType, totalEncounters, total: _coTravelerResults.length };
  }, [_coTravelerResults]);

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <GroupIcon color="primary" sx={{ fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight="bold">
          Co-Traveler Analysis
        </Typography>
        {_coTravelerResults.length > 0 && (
          <Chip 
            label={`${_coTravelerResults.length} found`} 
            size="small" 
            color="success"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      {/* Source selector (only when AdTech module is available) */}
      {adtechEnabled && (
        <Box sx={{ mb: 1.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={source}
            onChange={(_, v) => { if (v) setSource(v); }}
            disabled={isAnalyzing}
          >
            <ToggleButton value="track" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
              GPS Tracks
            </ToggleButton>
            <ToggleButton value="adtech" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
              AdTech
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Compact Parameters */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Typography variant="caption" color="text.secondary">
            {source === 'adtech' ? 'Search radius' : 'Proximity'}: {params.proximityThreshold}m
          </Typography>
          <Slider
            size="small"
            value={params.proximityThreshold}
            onChange={(_, value) => setParams(prev => ({ ...prev, proximityThreshold: value as number }))}
            min={10}
            max={500}
            step={10}
            disabled={isAnalyzing}
            sx={{ py: 0.5 }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Typography variant="caption" color="text.secondary">
            Time Window: ±{params.timeThreshold}min
          </Typography>
          <Slider
            size="small"
            value={params.timeThreshold}
            onChange={(_, value) => setParams(prev => ({ ...prev, timeThreshold: value as number }))}
            min={1}
            max={60}
            disabled={isAnalyzing}
            sx={{ py: 0.5 }}
          />
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleStartAnalysis}
          disabled={
            isAnalyzing ||
            (source === 'adtech'
              ? selectedStudents.length === 0
              : params.deviceIds.length === 0 || dataPoints.length === 0)
          }
          startIcon={<PlayIcon />}
          sx={{ fontSize: '0.75rem' }}
        >
          {isAnalyzing ? 'Analyzing...' : 'Analyze'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleStopAnalysis}
          disabled={!isAnalyzing}
          startIcon={<StopIcon />}
          sx={{ fontSize: '0.75rem' }}
        >
          Stop
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleExportResults}
          disabled={_coTravelerResults.length === 0}
          startIcon={<DownloadIcon />}
          sx={{ fontSize: '0.75rem' }}
        >
          Export
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onVisualizationUpdate?.([])}
          disabled={_coTravelerResults.length === 0}
          startIcon={<RefreshIcon />}
          sx={{ fontSize: '0.75rem' }}
        >
          Clear
        </Button>
      </Box>

      {/* Progress */}
      {isAnalyzing && (
        <Box sx={{ mb: 1.5 }}>
          <LinearProgress variant={progress < 0 ? "indeterminate" : "determinate"} value={progress < 0 ? undefined : progress} />
          <Typography variant="caption" color="text.secondary">{loadingMessage}</Typography>
        </Box>
      )}

      {/* Divider between controls and results */}
      {(_coTravelerResults.length > 0 || isAnalyzing) && (
        <Divider sx={{ my: 2 }} />
      )}

      {/* Summary Stats */}
      {summaryStats && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          {(Object.entries(summaryStats.byType) as [keyof typeof ENTITY_COLORS, number][]).map(([type, count]) => count > 0 && (
            <Chip
              key={type}
              label={`${type}: ${count}`}
              size="small"
              sx={{ 
                bgcolor: alpha(ENTITY_COLORS[type] || '#9e9e9e', 0.2),
                color: ENTITY_COLORS[type] || '#9e9e9e',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                height: 22
              }}
            />
          ))}
          <Chip
            label={`${summaryStats.totalEncounters.toLocaleString()} encounters`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Box>
      )}

      {/* Timeline Heatmap Chart */}
      {heatmapData.entities.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Encounter Timeline (Hour of Day)
          </Typography>
          <Box ref={chartContainerRef} sx={{ position: 'relative', width: '100%' }}>
            <Box ref={containerRef} sx={{ position: 'relative' }}>
            <svg width={width} height={height}>
              <Group left={margin.left} top={margin.top}>
                {/* Y-axis labels (entities) */}
                {heatmapData.entities.map(entity => (
                  <text
                    key={entity.id}
                    x={-8}
                    y={(yScale(entity.id) || 0) + yScale.bandwidth() / 2}
                    textAnchor="end"
                    alignmentBaseline="middle"
                    fontSize={10}
                    fill={ENTITY_COLORS[entity.type] || theme.palette.text.secondary}
                    fontWeight="bold"
                  >
                    {entity.id.length > 15 ? entity.id.slice(0, 15) + '...' : entity.id}
                  </text>
                ))}

                {/* Heatmap cells */}
                {heatmapData.hourlyData.map((d, i) => {
                  const y = yScale(d.entity) || 0;
                  const cellWidth = innerWidth / 24;
                  const color = ENTITY_COLORS[d.entityType] || '#9e9e9e';
                  
                  return (
                    <rect
                      key={i}
                      x={xScale(d.hour)}
                      y={y}
                      width={cellWidth - 1}
                      height={yScale.bandwidth()}
                      fill={color}
                      fillOpacity={colorScale(d.count)}
                      rx={2}
                      style={{ cursor: 'pointer' }}
                      onMouseMove={(event) => {
                        const coords = localPoint(event);
                        showTooltip({
                          tooltipData: {
                            entity: d.entity,
                            entityType: d.entityType,
                            hour: d.hour,
                            encounters: d.count,
                            date: d.date
                          },
                          tooltipLeft: coords?.x || 0,
                          tooltipTop: coords?.y || 0,
                        });
                      }}
                      onMouseLeave={hideTooltip}
                    />
                  );
                })}

                {/* X-axis (hours) */}
                <AxisBottom
                  scale={xScale}
                  top={innerHeight}
                  tickValues={[0, 6, 12, 18, 23]}
                  tickFormat={(v) => `${v}:00`}
                  stroke={theme.palette.divider}
                  tickStroke={theme.palette.divider}
                  tickLabelProps={() => ({
                    fill: theme.palette.text.secondary,
                    fontSize: 10,
                    textAnchor: 'middle',
                  })}
                />
              </Group>
            </svg>

            {/* Tooltip */}
            {tooltipOpen && tooltipData && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 4,
                  padding: '8px 12px',
                  boxShadow: theme.shadows[4],
                }}
              >
                <Typography variant="caption" fontWeight="bold" display="block">
                  {tooltipData.entity}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {tooltipData.hour}:00 - {tooltipData.hour + 1}:00
                </Typography>
                <Typography variant="caption" sx={{ color: ENTITY_COLORS[tooltipData.entityType] }}>
                  {tooltipData.encounters} encounters
                </Typography>
              </TooltipInPortal>
            )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {_coTravelerResults.length === 0 && !isAnalyzing && dataPoints.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          <Typography variant="caption">
            Click "Analyze" to find co-travelers in the loaded data
          </Typography>
        </Box>
      )}

      {dataPoints.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          <Typography variant="caption">
            Load GPS data first to run co-traveler analysis
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EnhancedCoTravelerAnalysis;
